package shared.utils

import kotlin.js.Date
import features.members.domain.Member

/**
 * CSV Utilities for Member Import/Export
 */
object CSVUtils {
    
    /**
     * Parse CSV text into member data with validation
     */
    fun parseCSV(csvText: String): ParseResult {
        val lines = csvText.trim().split("\n").map { it.trim() }
        
        if (lines.isEmpty()) {
            return ParseResult(emptyList(), listOf("CSV file is empty"))
        }
        
        // Parse header
        val header = lines[0].split(",").map { it.trim().lowercase() }
        val nameIndex = header.indexOf("name")
        val emailIndex = header.indexOf("email")
        val roleIndex = header.indexOf("role")
        val joinDateIndex = header.indexOf("joindate")
        val quotaIndex = header.indexOf("quota")
        
        if (nameIndex == -1 || emailIndex == -1) {
            return ParseResult(emptyList(), listOf("CSV must have 'name' and 'email' columns"))
        }
        
        val validMembers = mutableListOf<Member>()
        val errors = mutableListOf<String>()
        val emails = mutableSetOf<String>()
        
        // Parse data rows (skip header)
        for (i in 1 until lines.size) {
            val line = lines[i]
            if (line.isBlank()) continue
            
            val columns = parseCSVLine(line)
            val rowNum = i + 1
            
            // Extract values
            val name = columns.getOrNull(nameIndex)?.trim() ?: ""
            val email = columns.getOrNull(emailIndex)?.trim() ?: ""
            val role = columns.getOrNull(roleIndex)?.trim()?.lowercase() ?: "member"
            val joinDate = columns.getOrNull(joinDateIndex)?.trim() ?: ""
            val quotaStr = columns.getOrNull(quotaIndex)?.trim() ?: "12"
            
            // Validate
            val rowErrors = mutableListOf<String>()
            
            if (name.isBlank()) {
                rowErrors.add("Row $rowNum: Name is required")
            }
            
            if (email.isBlank()) {
                rowErrors.add("Row $rowNum: Email is required")
            } else if (!isValidEmail(email)) {
                rowErrors.add("Row $rowNum: Invalid email format '$email'")
            } else if (emails.contains(email)) {
                rowErrors.add("Row $rowNum: Duplicate email '$email'")
            }
            
            if (role !in listOf("owner", "admin", "member")) {
                rowErrors.add("Row $rowNum: Role must be 'owner', 'admin', or 'member' (got '$role')")
            }
            
            if (joinDate.isNotBlank() && !isValidDate(joinDate)) {
                rowErrors.add("Row $rowNum: Invalid date format '$joinDate' (use YYYY-MM-DD)")
            }
            
            val quota = quotaStr.toIntOrNull()
            if (quota == null || quota < 0) {
                rowErrors.add("Row $rowNum: Quota must be a positive number (got '$quotaStr')")
            }
            
            if (rowErrors.isEmpty()) {
                // Valid member
                val memberId = "user_" + Date.now().toLong().toString() + "_" + i
                validMembers.add(
                    Member(
                        id = memberId,
                        name = name,
                        email = email,
                        role = role,
                        joinDate = joinDate.ifBlank { null },
                        quota = quota ?: 12
                    )
                )
                emails.add(email)
            } else {
                errors.addAll(rowErrors)
            }
        }
        
        return ParseResult(validMembers, errors)
    }
    
    /**
     * Parse a single CSV line handling quoted values
     */
    private fun parseCSVLine(line: String): List<String> {
        val result = mutableListOf<String>()
        var current = StringBuilder()
        var inQuotes = false
        
        for (char in line) {
            when {
                char == '"' -> inQuotes = !inQuotes
                char == ',' && !inQuotes -> {
                    result.add(current.toString())
                    current = StringBuilder()
                }
                else -> current.append(char)
            }
        }
        result.add(current.toString())
        
        return result
    }
    
    /**
     * Validate email format (basic)
     */
    private fun isValidEmail(email: String): Boolean {
        val regex = Regex("^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}\$")
        return regex.matches(email)
    }
    
    /**
     * Validate date format YYYY-MM-DD
     */
    private fun isValidDate(date: String): Boolean {
        val regex = Regex("^\\d{4}-\\d{2}-\\d{2}\$")
        if (!regex.matches(date)) return false
        
        // Additional validation: check if date parts are valid
        val parts = date.split("-")
        val year = parts[0].toIntOrNull() ?: return false
        val month = parts[1].toIntOrNull() ?: return false
        val day = parts[2].toIntOrNull() ?: return false
        
        if (year < 1900 || year > 2100) return false
        if (month < 1 || month > 12) return false
        if (day < 1 || day > 31) return false
        
        return true
    }
    
    /**
     * Generate CSV text from member list
     */
    fun generateCSV(members: List<Member>): String {
        val lines = mutableListOf<String>()
        
        // Header (simplified - removed joinDate and quota)
        lines.add("name,email,role")
        
        // Data rows
        members.forEach { member ->
            val name = escapeCSV(member.name)
            val email = escapeCSV(member.email)
            val role = member.role
            
            lines.add("$name,$email,$role")
        }
        
        return lines.joinToString("\n")
    }
    
    /**
     * Escape CSV value (add quotes if contains comma)
     */
    private fun escapeCSV(value: String): String {
        return if (value.contains(",") || value.contains("\"")) {
            "\"${value.replace("\"", "\"\"")}\""
        } else {
            value
        }
    }
    
    /**
     * Generate empty CSV template
     */
    fun generateTemplate(): String {
        return """name,email,role
John Doe,john@example.com,member
Jane Smith,jane@example.com,admin"""
    }
}

/**
 * Result of CSV parsing
 */
data class ParseResult(
    val validMembers: List<Member>,
    val errors: List<String>
)
